/**
 * Auth→Firestore credential handoff helpers.
 *
 * Firestore security rules see request.auth=null until the Auth token has
 * propagated into the Firestore client. That race shows up as
 * permission-denied right after sign-in — especially on cold starts / school
 * browsers with no IndexedDB cache, and for accounts with useShareCode=false
 * (owner-only reads).
 */

export const isFirestorePermissionDeniedError = (error) => {
  if (!error) return false;
  if (error.code === "permission-denied") return true;
  return /missing or insufficient permissions/i.test(String(error.message || ""));
};

export const isFirebaseAuthNetworkError = (error) => {
  if (!error) return false;
  if (error.code === "auth/network-request-failed") return true;
  return /auth\/network-request-failed|network request failed/i.test(
    String(error.message || "")
  );
};

const waitForCredentialHandoff = (delayMs) =>
  new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });

const AUTH_NETWORK_RETRY_DELAYS_MS = [0, 400, 1200, 2500];

/**
 * Wait until Auth has a current user (if any) and its ID token is available.
 * @param {import('firebase/auth').Auth} auth
 * @param {{
 *   forceRefresh?: boolean,
 *   credentialHandoffDelayMs?: number,
 *   networkRetryDelaysMs?: number[],
 *   waitForHandoff?: (delayMs: number) => Promise<void>,
 *   waitForRetry?: (delayMs: number) => Promise<void>
 * }} [options]
 */
export const ensureFirestoreAuthReady = async (auth, options = {}) => {
  const {
    forceRefresh = false,
    credentialHandoffDelayMs = 100,
    networkRetryDelaysMs = AUTH_NETWORK_RETRY_DELAYS_MS,
    waitForHandoff = waitForCredentialHandoff,
    waitForRetry = waitForCredentialHandoff,
  } = options;

  if (typeof auth?.authStateReady === "function") {
    await auth.authStateReady();
  }

  let user = auth?.currentUser || null;
  if (!user && typeof auth?.onAuthStateChanged === "function") {
    user = await new Promise((resolve) => {
      let unsubscribe = () => {};
      unsubscribe = auth.onAuthStateChanged((nextUser) => {
        unsubscribe();
        resolve(nextUser);
      });
    });
  }

  if (!user) {
    return null;
  }

  const retryDelays = Array.isArray(networkRetryDelaysMs) && networkRetryDelaysMs.length > 0
    ? networkRetryDelaysMs
    : [0];
  let lastError = null;
  for (let attempt = 0; attempt < retryDelays.length; attempt += 1) {
    const delayMs = retryDelays[attempt];
    if (delayMs > 0) {
      await waitForRetry(delayMs);
    }

    try {
      await user.getIdToken(Boolean(forceRefresh));
      lastError = null;
      break;
    } catch (error) {
      lastError = error;
      const canRetry =
        isFirebaseAuthNetworkError(error)
        && attempt < retryDelays.length - 1;
      if (!canRetry) {
        throw error;
      }
    }
  }

  if (lastError) {
    throw lastError;
  }
  if (forceRefresh && credentialHandoffDelayMs > 0) {
    // getIdToken(true) resolves when Auth has the token. Firestore consumes it
    // through a separate observer; yield briefly before the next protected read.
    await waitForHandoff(credentialHandoffDelayMs);
  }
  return user;
};
